import React, { useEffect, useState, useCallback, useRef } from "react";
import styled, { css } from "styled-components";
import ButtonToolbar from "components/bootstrap/ButtonToolbar";
import { Link, LinkContainer, EntityList, EntityListItem, PaginatedList, SearchForm, Spinner } from "components/common";
import { Button, Col, DropdownButton, Label, MenuItem, Row, DeleteMenuItem } from "components/bootstrap";
import Routes from "routing/Routes";
import StringUtils from "util/StringUtils";
import NumberUtils from "util/NumberUtils";
import { useStore } from "stores/connect";
import { IndexSetDeletionForm, IndexSetDetails } from "components/indices";
import usePaginationQueryParameter from "hooks/usePaginationQueryParameter";
import { IndexSetsActions, IndexSetsStore } from "stores/indices/IndexSetsStore";
import { getPathnameWithoutId } from "util/URLUtils";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
const Toolbar = styled(Row)(
  ({ theme }) => css`
    border-bottom: 1px solid ${theme.colors.gray[90]};
    padding-bottom: ${theme.spacings.sm};
  `
);
const GlobalStatsCol = styled(Col)(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.xs};
  `
);
const GlobalStats = styled.p`
  margin-bottom: 0;
`;
const StatsInfoText = styled.span`
  font-style: italic;
`;
const formatStatsString = (stats) => {
  if (!stats) {
    return "N/A";
  }
  const indices = `${NumberUtils.formatNumber(stats.indices)} ${StringUtils.pluralize(stats.indices, "index", "indices")}`;
  const documents = `${NumberUtils.formatNumber(stats.documents)} ${StringUtils.pluralize(stats.documents, "document", "documents")}`;
  const size = NumberUtils.formatBytes(stats.size);
  return `${indices}, ${documents}, ${size}`;
};
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 10;
const SEARCH_MIN_TERM_LENGTH = 3;
const IndexSetsComponent = () => {
  const { indexSetsCount, indexSets, indexSetStats, globalIndexSetStats } = useStore(IndexSetsStore);
  const { page, resetPage } = usePaginationQueryParameter();
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();
  const [statsEnabled, setStatsEnabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState(void 0);
  const formsRef = useRef();
  const loadData = useCallback(
    (pageNumber = DEFAULT_PAGE_NUMBER, limit = DEFAULT_PAGE_SIZE) => {
      if (searchTerm) {
        IndexSetsActions.searchPaginated(searchTerm, (pageNumber - 1) * limit, limit, statsEnabled);
      } else {
        IndexSetsActions.listPaginated((pageNumber - 1) * limit, limit, statsEnabled);
      }
    },
    [statsEnabled, searchTerm]
  );
  useEffect(() => {
    loadData(page);
  }, [loadData, page]);
  useEffect(() => {
    if (statsEnabled) {
      IndexSetsActions.stats();
    }
  }, [statsEnabled]);
  const onSearch = (query) => {
    if (query && query.length >= SEARCH_MIN_TERM_LENGTH) {
      setSearchTerm(query);
      resetPage();
    } else if (!query || query.length === 0) {
      setSearchTerm(query);
      resetPage();
    }
  };
  const onSearchReset = () => {
    setSearchTerm(void 0);
    resetPage();
  };
  const onToggleStats = () => {
    setStatsEnabled(!statsEnabled);
  };
  const onSetDefault = (indexSet) => () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.INDICES.INDEX_SET_DEFAULT_SET, {
      app_pathname: getPathnameWithoutId(pathname),
      app_section: "index-sets",
      app_action_value: "set-default-index-set"
    });
    IndexSetsActions.setDefault(indexSet).then(() => loadData());
  };
  const onDelete = (indexSet) => () => {
    formsRef.current[`index-set-deletion-form-${indexSet.id}`].open();
  };
  const deleteIndexSet = (indexSet, deleteIndices) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.INDICES.INDEX_SET_DELETED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_section: "index-sets",
      app_action_value: "delete-index-set"
    });
    IndexSetsActions.delete(indexSet, deleteIndices).then(() => {
      resetPage();
      loadData(page);
    });
  };
  const statsDisabledText = "Stats are disabled by default";
  const formatIndexSet = (indexSet) => {
    const actions = /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(LinkContainer, { to: Routes.SYSTEM.INDEX_SETS.CONFIGURATION(indexSet.id) }, /* @__PURE__ */ React.createElement(Button, null, "Edit")), /* @__PURE__ */ React.createElement(DropdownButton, { title: "More Actions", id: `index-set-dropdown-${indexSet.id}`, pullRight: true }, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: onSetDefault(indexSet), disabled: !indexSet.can_be_default || indexSet.default }, "Set as default"), /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: onDelete(indexSet) })));
    const content = /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(IndexSetDetails, { indexSet }), /* @__PURE__ */ React.createElement(
      IndexSetDeletionForm,
      {
        ref: (elem) => {
          formsRef.current = { ...formsRef.current, [`index-set-deletion-form-${indexSet.id}`]: elem };
        },
        indexSet,
        onDelete: deleteIndexSet
      }
    ));
    const indexSetTitle = /* @__PURE__ */ React.createElement(Link, { to: Routes.SYSTEM.INDEX_SETS.SHOW(indexSet.id) }, indexSet.title);
    const isDefault = indexSet.default ? /* @__PURE__ */ React.createElement(Label, { key: `index-set-${indexSet.id}-default-label`, bsStyle: "primary" }, "default") : "";
    const isReadOnly = !indexSet.writable ? /* @__PURE__ */ React.createElement(Label, { key: `index-set-${indexSet.id}-readOnly-label`, bsStyle: "info" }, "read only") : "";
    let { description } = indexSet;
    if (indexSet.default) {
      description += `${description.endsWith(".") ? "" : "."} This index set will be used by default.`;
    }
    let statsString;
    const stats = indexSetStats[indexSet.id];
    if (stats) {
      statsString = formatStatsString(stats);
    }
    return /* @__PURE__ */ React.createElement(
      EntityListItem,
      {
        key: `index-set-${indexSet.id}`,
        title: indexSetTitle,
        titleSuffix: /* @__PURE__ */ React.createElement("span", null, statsEnabled ? statsString : /* @__PURE__ */ React.createElement(StatsInfoText, null, statsDisabledText), " ", isDefault, " ", isReadOnly),
        description,
        actions,
        contentRow: content
      }
    );
  };
  const isLoading = !indexSets;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
    SearchForm,
    {
      onSearch,
      queryWidth: 300,
      wrapperClass: "has-bm",
      onReset: onSearchReset,
      query: searchTerm,
      placeholder: "Find index sets"
    }
  ))), /* @__PURE__ */ React.createElement(Toolbar, null, /* @__PURE__ */ React.createElement(GlobalStatsCol, { md: 3 }, /* @__PURE__ */ React.createElement(GlobalStats, null, /* @__PURE__ */ React.createElement("strong", null, "Stats for all indices:"), " ", statsEnabled ? formatStatsString(globalIndexSetStats) : /* @__PURE__ */ React.createElement(StatsInfoText, null, statsDisabledText)), /* @__PURE__ */ React.createElement(Button, { onClick: onToggleStats }, statsEnabled ? "Disable stats" : "Enable stats"))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, isLoading ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(PaginatedList, { pageSize: DEFAULT_PAGE_SIZE, totalItems: indexSetsCount, showPageSizeSelect: false }, /* @__PURE__ */ React.createElement(
    EntityList,
    {
      bsNoItemsStyle: "info",
      noItemsText: "There are no index sets to display",
      items: indexSets.map((indexSet) => formatIndexSet(indexSet))
    }
  )))));
};
export default IndexSetsComponent;
